/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImageObj } from "@/types";

type ImageItem = {
  id: string;
  file: File;
  preview: string;
};
import { SetStateAction } from "react";

type ImageDropzoneProps = {
  label?: string;
  images: ImageObj[];
  setImages: (value: SetStateAction<ImageObj[]>) => void;
};
interface SortableImageProps {
  item: ImageItem;
  onRemove: (id: string) => void;
}

const SortableImage: React.FC<SortableImageProps> = ({ item, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        position: "relative",
        width: 300,
        height: 400,
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid #ddd",
        marginRight: 8,
      }}
      {...attributes}
      {...listeners}
    >
      <img
        src={item.preview}
        alt="preview"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
      <div
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.id);
        }}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          background: "red",
          padding: "2px 4px",
          border: "none",
          color: "white",
          cursor: "pointer",
        }}
        className="flex items-center justify-center"
      >
        delete
      </div>
    </div>
  );
};

const ImageDrop: React.FC<ImageDropzoneProps> = ({ images, setImages }) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newImages = acceptedFiles.map((file) => ({
      id: `${file.name}-${Date.now()}`,
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...newImages]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"] },
  });

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setImages((items: ImageObj[]) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleRemove = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  return (
    <div>
      {/* Dropzone */}
      <h5 className="label-style !text-[30px]">Images</h5>

      <div
        {...getRootProps()}
        style={{
          border: "2px dashed #ccc",
          padding: "20px",
          textAlign: "center",
          cursor: "pointer",
          borderRadius: 4,
        }}
      >
        <input {...getInputProps()} />
        <p>
          Drag & drop images here or click to select
          <br />
          <small>Supported formats: JPG, PNG, GIF, WebP</small>
        </p>
      </div>

      {/* Image list */}
      {images.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={images.map((img) => img.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 10,
                marginTop: 10,
              }}
            >
              {images.map((img, i) => (
                <div className="relative">
                  {i === 0 && (
                    <span className="absolute z-10 bg-black text-white p-1">
                      Cover image
                    </span>
                  )}

                  <SortableImage key={i} item={img} onRemove={handleRemove} />
                  {
                    <span className="absolute bottom-0 bg-black text-white p-1">
                      {i + 1}
                    </span>
                  }
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default ImageDrop;
